import React, { useEffect, useContext, useCallback } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import DSModal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import DSPopover from '@wix/da-ds/pkg/popovers/dsPresets/Popover';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import GroupsList from '../../../AddToGroupsPanel/_partials/GroupsList';
import { useAddToGroupFlow } from '../../../AddToGroupsPanel/useAddToGroupFlow';
import { GroupFolderId } from '../../../AddToGroupsPanel/redux/types';
import { PapiDeviation, PapiUser } from '@wix/da-papi-types';
import FolderList from '../../../AddToGroupsPanel/_partials/FolderList';
import SelectedGroupHeader from './SelectedGroupHeader';
import GroupSearchInput from './GroupSearchInput';
import s from './AddToGroupPopup.scss';

export interface Props {
  deviation: PapiDeviation;
  hasNoGroups: boolean;
  hasOnlyOneChunkOfGroupsToLoad: boolean;
  className?: string;
  biData?: any;
  actionbarType?: 'primary' | 'secondary';
  forceModal?: boolean;
  initGroupsPanel: () => void;
  addToGroupFolder: (
    deviation: PapiDeviation,
    group: PapiUser,
    groupFolderId: GroupFolderId,
    actionbarType?: string,
    biData?: any
  ) => void;
  onClose?: () => void;
}

const AddToGroupPopup: React.FC<Props> = ({
  deviation,
  className,
  biData = {},
  actionbarType,
  initGroupsPanel,
  addToGroupFolder,
  hasOnlyOneChunkOfGroupsToLoad,
  hasNoGroups,
  forceModal,
  onClose,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);

  const {
    selectGroup,
    selectedGroup,
    resetSelectedGroup,
    selectedSubmissionType,
    filterValue,
    onFilterInputChange,
  } = useAddToGroupFlow();

  useEffect(() => {
    initGroupsPanel();
  }, [initGroupsPanel]);

  const onFolderSelected = useCallback(
    folderId => {
      if (!selectedGroup) {
        return;
      }

      addToGroupFolder(
        deviation,
        selectedGroup,
        {
          groupId: selectedGroup.userId,
          type: selectedSubmissionType,
          folderId,
        },
        actionbarType,
        biData
      );
      setTimeout(() => {
        onClose && onClose();
      }, 1000); // to show state change (folder becoming checked in the list) for the user
    },
    [
      actionbarType,
      addToGroupFolder,
      deviation,
      biData,
      onClose,
      selectedGroup,
      selectedSubmissionType,
    ]
  );

  const showSearch =
    !selectedGroup && !hasNoGroups && hasOnlyOneChunkOfGroupsToLoad;

  const preamble = (
    <>
      {showSearch && (
        <GroupSearchInput
          className={s['filter-input']}
          filterValue={filterValue}
          onFilterInputChange={onFilterInputChange}
        />
      )}
    </>
  );

  const content = (
    <BiLoggerContextProvider value={biData}>
      {selectedGroup === undefined && (
        <GroupsList
          deviationId={deviation.deviationId}
          filterValue={filterValue}
          onSelect={selectGroup}
        />
      )}
      {selectedGroup && (
        <FolderList
          deviationId={deviation.deviationId}
          groupId={selectedGroup.userId}
          submissionType={selectedSubmissionType}
          onFolderSelected={onFolderSelected}
        />
      )}
    </BiLoggerContextProvider>
  );

  if (isMobile || forceModal) {
    return (
      <DSModal
        isOpen
        heading={
          selectedGroup ? (
            <SelectedGroupHeader
              group={selectedGroup}
              resetSelectedGroup={resetSelectedGroup}
            />
          ) : (
            <>{t('addToGroupPopup.title')}</>
          )
        }
        preamble={isMobile ? preamble : undefined}
        className={c(s['root'], !isMobile && s['forced-modal'], className)}
        contentClassName={s['content-wrapper']}
        layoutClassName={c(!isMobile && s['forced-modal-layout'])}
        withScrollableContentWrapper={true}
        onRequestClose={onClose}
      >
        {isMobile ? undefined : preamble}
        {content}
      </DSModal>
    );
  }

  return (
    <DSPopover
      heading={
        selectedGroup ? selectedGroup.username : t('addToGroupPopup.title')
      }
      className={c(s['root'], className)}
      contentClassName={s['content-wrapper']}
      preamble={preamble}
      content={content}
      onCloseClick={onClose}
      onBackClick={selectedGroup ? resetSelectedGroup : undefined}
    />
  );
};
AddToGroupPopup.displayName = 'AddToGroupPopup';

export default AddToGroupPopup;
